import { addressSchema } from '@commenda/schema/address';
import { YesNoNotSureOptions } from '@commenda/types/src/common/enums';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import {
  CorporationType,
  LegacyDocumentType,
} from '@prisma/client';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as z from 'zod';

const urlRegex = /^(https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}([\/#?]?[\w.-]+)*\/?$/;

export const companyInfoSchema = z
  .object({
    corporationName: z
      .string()
      .trim()
      .min(1, { message: 'Corporation name should not be empty.' }),
    companyLegalName2: z.string().trim().optional(),
    companySuffix: z.string().trim(),
    // The content's jurisdiction id. Which states exist is the content's answer and the backend
    // refuses one it does not carry, so this checks that one was chosen.
    jurisdiction: z.string().min(1, 'Please choose a jurisdiction.'),
    socialLink: z
      .string()
      .trim()
      .regex(urlRegex, { message: 'Please enter a valid URL.' }),
    businessDescription: z.string().trim().min(25, {
      message: 'Please enter a description with at least 25 characters.',
    }),
    fiscalMonth: z
      .string()
      .min(1, { message: 'Please select a fiscal year start month.' }),
    optForRegisterredAddressService: z.boolean().default(false),
    mailingAddressOption: z
      .enum(['service', 'manual', 'sameAsPrincipal'])
      .default('manual')
      .optional(),
    mailingAddress: addressSchema.optional(),
    operatingAddress: addressSchema,
    corporationType: z.nativeEnum(CorporationType, {
      errorMap: () => ({ message: 'Please select an entity type.' }),
    }),
    customBusinessPurpose: z.string().trim().optional(),
    isTaxpayerInUS: z.boolean(),
    isMemberManagedOrManagerManaged: z.string().optional(),
    planToHireUSEmployeesInFirst12Months: z
      .nativeEnum(YesNoNotSureOptions)
      .optional(),

    numberOfEmployees: z
      .union([
        z
          .number({
            invalid_type_error:
              'This field must be a number greater than or equal to 1.',
          })
          .int({
            message: 'This field must be a number greater than or equal to 1.',
          })
          .min(1, {
            message: 'This field must be a number greater than or equal to 1.',
          }),
        z.literal(''),
      ])
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mailingAddressOption === 'manual') {
      const m = data.mailingAddress;
      if (!m) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Mailing address is required',
          path: ['mailingAddress'],
        });
      } else {
        if (!m.addressLine1 || `${m.addressLine1}`.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Address Line 1 is required',
            path: ['mailingAddress', 'addressLine1'],
          });
        }
        if (!m.city || `${m.city}`.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'City is required',
            path: ['mailingAddress', 'city'],
          });
        }
        if (!m.state || `${m.state}`.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'State is required',
            path: ['mailingAddress', 'state'],
          });
        }
        if (!m.postalCode || `${m.postalCode}`.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Postal Code is required',
            path: ['mailingAddress', 'postalCode'],
          });
        }
        if (!m.country || `${m.country}`.toString().trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Country is required',
            path: ['mailingAddress', 'country'],
          });
        }
      }
    }

    if (
      data.corporationType === CorporationType.LLC &&
      (!data.companyLegalName2 || data.companyLegalName2.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a fallback company name.',
        path: ['companyLegalName2'],
      });
    }

    if (data.planToHireUSEmployeesInFirst12Months === YesNoNotSureOptions.Yes) {
      if (
        data.numberOfEmployees === undefined ||
        data.numberOfEmployees === ''
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter the approximate number of employees.',
          path: ['numberOfEmployees'],
        });
      } else if (
        typeof data.numberOfEmployees === 'number' &&
        data.numberOfEmployees < 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'This field must be a number greater than or equal to 1',
          path: ['numberOfEmployees'],
        });
      }
    }
  });

export const directorSchema = z.object({
  directorFirstName: z
    .string()
    .trim()
    .min(1, { message: 'Please enter a valid first name' }),
  directorLastName: z.string().trim().nullable(),

  number: z
    .string()
    .trim()
    .min(1, { message: 'Please enter a valid phone number' })
    .refine((value) => isValidPhoneNumber(value), {
      message: 'Please enter a valid phone number',
    }),
  email: z.string().email({ message: 'Please enter a valid email address' }),
  dateOfBirth: z
    .string()
    .min(1, { message: 'Please enter a valid date of birth' })
    .refine(
      (value) => {
        if (!value) return false;
        const currentDate = NonTimezoneSensitiveDate.fromNativeDate(new Date());
        const nonTimeZoneSensitiveDate =
          NonTimezoneSensitiveDate.fromNativeDate(new Date(value));
        return currentDate.gte(nonTimeZoneSensitiveDate);
      },
      { message: 'Date of birth must not be in the future.' }
    ),
  personalDetails: z.object({
    nationality: z.string().min(1, { message: 'Please select nationality' }),
    educationalQualification: z
      .string()
      .min(1, { message: 'Please select educational qualification' })
      .optional(),
  }),
  passportProof: z.object({
    id: z.number().optional(),
    documentType: z.nativeEnum(LegacyDocumentType),
    fileName: z.string(),
    fileUrl: z.string(),
  }),
  operatingAddress: addressSchema,
  corporationHasDirector: z
    .array(
      z.object({
        corporationId: z.number(),
        // Null on 68 of production's 1,372 links: the column is Json? and nothing requires it.
        // A director is judged on their own details, so a link that records nothing is not a gap
        // in them — and the list carries links to other companies' entities too.
        additionalDirectorInformation: z
          .object({
            isAuthorizedSignatory: z.boolean().optional(),
            isManager: z.boolean().optional(),
          })
          .nullable(),
      })
    )
    .optional(),
});

export const responsiblePartyInfoSchema = z
  .object({
    directorFirstName: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a valid first name' }),
    directorLastName: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a valid last name' }),
    hasSSNorITIN: z.boolean().optional(),
    SSNOrITIN: z.string().trim().optional(),
    __ssnInitialMasked: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.hasSSNorITIN) return;

    const value = data.SSNOrITIN ?? '';
    const digitsOnly = value.replace(/-/g, '');
    const maskedPattern = /^-{5}\d{4}$/;

    // 1) If it's the exact original masked string, allow (unchanged SSN)
    if (
      maskedPattern.test(value) &&
      value === (data.__ssnInitialMasked ?? '')
    ) {
      return;
    }

    // 2) Otherwise, require a full 9-digit value (with or without hyphens)
    if (!/^\d{9}$/.test(digitsOnly)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['SSNOrITIN'],
        message:
          'Please enter a valid 9-digit SSN or 9-digit ITIN (with or without hyphens)',
      });
    }
  });

export type ResponsiblePartyInfoFormFields = z.infer<
  typeof responsiblePartyInfoSchema
>;

export type CompanyInfoFormFields = z.infer<typeof companyInfoSchema>;

export type DirectorSchemaFields = z.infer<typeof directorSchema>;
